import { movies } from "@/mocks/movies";

export type Movie = (typeof movies)[number];

export interface MovieFilters {
  type?: string;
  genre?: string;
  region?: string;
  year?: string;
}

export type SortKey = "default" | "updated" | "rating" | "hot";

export function getAllMovies(): Movie[] {
  return movies;
}

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((item) => item.id === id);
}

export function filterMovies(list: Movie[], filters: MovieFilters): Movie[] {
  return list.filter((item) => {
    if (filters.type && item.type !== filters.type) return false;
    if (filters.genre && !item.genres.includes(filters.genre)) return false;
    if (filters.region && item.region !== filters.region) return false;
    if (filters.year && item.year !== Number(filters.year)) return false;
    return true;
  });
}

export function sortMovies(list: Movie[], key: SortKey): Movie[] {
  const sorted = [...list];
  switch (key) {
    case "updated":
      return sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    case "rating":
      return sorted.sort((a, b) => b.rating - a.rating);
    case "hot":
      return sorted.sort((a, b) => b.hotness - a.hotness);
    default:
      return sorted.sort(
        (a, b) => b.rating * 10 + b.hotness / 10 - (a.rating * 10 + a.hotness / 10),
      );
  }
}

export function searchMovies(list: Movie[], keyword: string): Movie[] {
  const word = keyword.trim().toLowerCase();
  if (!word) return [];
  return list.filter((item) => {
    const haystack = [
      item.title,
      item.director,
      item.region,
      item.genres.join(" "),
      item.castList.join(" "),
      item.synopsis,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(word);
  });
}

export function getRelatedMovies(list: Movie[], current: Movie): Movie[] {
  return list
    .filter((item) => item.id !== current.id)
    .map((item) => {
      const genreScore = item.genres.filter((g) => current.genres.includes(g)).length;
      const typeScore = item.type === current.type ? 1 : 0;
      return { item, score: genreScore * 2 + typeScore };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, 5)
    .map((entry) => entry.item);
}

export function getHotMovies(limit = 6): Movie[] {
  return [...movies].sort((a, b) => b.hotness - a.hotness).slice(0, limit);
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, limit);
}